import {
    defineConnectorConstellationDerivation,
    defineConnectorItemCollectionDerivation,
    defineConnectorItemDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildLineGuideShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    ConnectorConstellationDerivation,
    ConnectorItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toNaturalRenderedDigits } from '@/adapters/primitive/_pure/derivation/itemFactories';
import {
    toNaturalAxialTieCollectionKey,
    toNaturalAxialTieConstellationKey,
    toNaturalAxialTieItemKey,
    toNaturalDigitItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toDepthOrderedWheels, toPrimitiveBandOpacity } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

/** Static endpoints follow the full layout Euler, unlike ring-intersection endpoints. */
function toSlotEndpoint(wheelRole: PrimitiveWheelRole, power: number, digit: number) {
    return {
        endpointKind: 'static' as const,
        itemKey: toNaturalDigitItemKey({ wheelRole, power, digit }),
        anchorKey: 'arcMid' as const,
    };
}

export function buildNaturalAxialTieItemCollectionDerivation(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
): ConnectorItemCollectionDerivation | null {
    const depthOrderedWheels = toDepthOrderedWheels(wheels.filter((wheel) => wheel.variant === undefined));
    const renderedDigits = toNaturalRenderedDigits(config);
    const itemDerivations = [];
    const emittedItemKeys = new Set<string>();

    for (let index = 1; index < depthOrderedWheels.length; index += 1) {
        const nearWheel = depthOrderedWheels[index - 1];
        const farWheel = depthOrderedWheels[index];
        if (nearWheel === undefined || farWheel === undefined) continue;
        if (nearWheel.role === farWheel.role) continue;

        for (const power of nearWheel.powers) {
            if (!farWheel.powers.includes(power)) continue;

            for (const digit of renderedDigits) {
                const itemKey = toNaturalAxialTieItemKey({
                    nearRole: nearWheel.role,
                    farRole: farWheel.role,
                    power,
                    digit,
                });
                if (emittedItemKeys.has(itemKey)) continue;
                emittedItemKeys.add(itemKey);

                itemDerivations.push(defineConnectorItemDerivation({
                    item: {
                        itemKind: 'connector',
                        itemKey,
                        shape: buildLineGuideShape(),
                        endpoints: {
                            from: toSlotEndpoint(nearWheel.role, power, digit),
                            to: toSlotEndpoint(farWheel.role, power, digit),
                        },
                    },
                    parametricState: {
                        visualStyle: {
                            color: config.natural.display.marker.axialTie.color,
                            opacity: config.natural.display.marker.axialTie.opacity * Math.min(
                                toPrimitiveBandOpacity(nearWheel, power),
                                toPrimitiveBandOpacity(farWheel, power),
                            ),
                        },
                    },
                }));
            }
        }
    }

    if (itemDerivations.length === 0) return null;

    return defineConnectorItemCollectionDerivation({
        itemCollectionKey: toNaturalAxialTieCollectionKey(),
        itemCollectionKind: 'connector',
        itemDerivations,
    });
}

export function buildNaturalAxialTieConstellationDerivation(): ConnectorConstellationDerivation {
    return defineConnectorConstellationDerivation({
        constellation: {
            constellationKind: 'connector',
            constellationKey: toNaturalAxialTieConstellationKey(),
            itemCollectionKey: toNaturalAxialTieCollectionKey(),
        },
        parametricState: {
            visibility: { visible: true },
        },
    });
}
